import {PageMap} from '@wix/wixstores-client-core';
import {SiteStore} from '@wix/wixstores-client-storefront-sdk';
import {ControllerParams} from '@wix/yoshi-flow-editor';
import {BIService} from '../services/BIService';
import {CartService} from './CartService';
import {ContinueShoppingBiOrigin} from '../../common/constants';
import {ICartControllerApi} from '../../types/app.types';
import {ModalManagerService} from './ModalManagerService';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';
import {SPECS} from '../specs';

export class NavigationService {
  private readonly controllerApi: ICartControllerApi;
  public get isNavigationToCheckoutInProcess(): boolean {
    return this._isNavigationToCheckoutInProcess;
  }

  public set isNavigationToCheckoutInProcess(value: boolean) {
    this._isNavigationToCheckoutInProcess = value;
    void this.controllerApi.updateComponent();
  }
  private readonly siteStore: SiteStore;
  private readonly viewerContext: ViewerContext;
  private readonly biService: BIService;
  private readonly publicData: ControllerParams['controllerConfig']['config']['publicData'];
  private readonly windowApi: ControllerParams['controllerConfig']['wixCodeApi']['window'];
  private readonly cartService: CartService;
  public readonly modalManagerService: ModalManagerService;
  private _isNavigationToCheckoutInProcess: boolean = false;

  constructor({
    siteStore,
    viewerContext,
    biService,
    cartService,
    publicData,
    controllerApi,
    modalManagerService,
    controllerParams,
  }: {
    siteStore: SiteStore;
    viewerContext: ViewerContext;
    biService: BIService;
    cartService: CartService;
    modalManagerService: ModalManagerService;
    publicData: ControllerParams['controllerConfig']['config']['publicData'];
    controllerApi: ICartControllerApi;
    controllerParams: ControllerParams;
  }) {
    this.siteStore = siteStore;
    this.viewerContext = viewerContext;
    this.cartService = cartService;
    this.biService = biService;
    this.publicData = publicData;
    this.controllerApi = controllerApi;
    this.modalManagerService = modalManagerService;
    this.windowApi = controllerParams.controllerConfig.wixCodeApi.window;
  }

  public readonly closeSideCart = () => {
    (this.windowApi.lightbox as any).close();
  };

  private get continueShoppingLinkObject() {
    return this.publicData.COMPONENT.CART_CONTINUE_SHOPPING_LINK_OBJECT;
  }

  public readonly continueShopping = async (origin = ContinueShoppingBiOrigin.shoppingCart) => {
    this.biService.cartClickOnContinueShoppingSf(this.cartService.cartModel, this.cartService.estimatedTotals, origin);
    const moveCartToViewerContext = this.viewerContext.isExperimentEnabled(SPECS.MoveCartToViewerContext);
    if (moveCartToViewerContext) {
      const url = this.getContinueShoppingUrlWithFallback();
      return this.viewerContext.navigateToUrl(url);
    } else {
      const link = await this.getLink();
      return this.siteStore.navigateToLink(link);
    }
  };

  public readonly navigateToCart = async () => {
    this.biService.clickToViewCartPage(this.cartService.cartModel, this.cartService.cartType);
    const to = {sectionId: PageMap.CART};
    const moveCartToViewerContext = this.viewerContext.isExperimentEnabled(SPECS.MoveCartToViewerContext);
    return moveCartToViewerContext
      ? this.viewerContext.navigateToSection(PageMap.CART)
      : this.siteStore.navigate(to, true);
  };

  private getLink() {
    return this.continueShoppingLinkObject
      ? this.siteStore.getLink(this.continueShoppingLinkObject)
      : this.siteStore.getHomepageLink();
  }

  private getContinueShoppingUrlWithFallback() {
    const linkUrl = this.viewerContext.getLinkUrl(this.continueShoppingLinkObject);
    return linkUrl ?? this.viewerContext.getHomepageUrl();
  }

  public async getContinueShoppingHref() {
    const moveCartToViewerContext = this.viewerContext.isExperimentEnabled(SPECS.MoveCartToViewerContext);
    try {
      return moveCartToViewerContext ? this.getContinueShoppingUrlWithFallback() : (await this.getLink()).url;
    } catch {
      //
    }
    /* istanbul ignore next */
    return '#';
  }

  /* istanbul ignore next */
  public readonly navigateToProduct = (urlPart: string) => {
    if (this.viewerContext.isExperimentEnabled(SPECS.UseLinkFromCartItem)) {
      return undefined;
    }
    const to = {sectionId: PageMap.PRODUCT, state: urlPart};
    const moveCartToViewerContext = this.viewerContext.isExperimentEnabled(SPECS.MoveCartToViewerContext);
    return moveCartToViewerContext
      ? this.viewerContext.navigateToSection(PageMap.PRODUCT)
      : this.siteStore.navigate(to, true);
  };

  public readonly navigateToUrl = (url: string) => {
    const moveCartToViewerContext = this.viewerContext.isExperimentEnabled(SPECS.MoveCartToViewerContext);

    return moveCartToViewerContext
      ? this.viewerContext.navigateToUrl(url)
      : this.siteStore.navigateToLink({
          sdkLink: {type: 'ExternalLink', target: '_top', url},
          url,
        });
  };
}
